import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Rating } from 'primeng/rating';

/**
 * Rattoppi di accessibilita' per PrimeNG 17.5, applicati a valle perche' il
 * markup lo genera la libreria e non e' modificabile alla sorgente.
 *
 * 1. TENDINE — `role="combobox"` con `aria-controls="pn_id_N_list"`, ma la
 *    lista viene costruita solo all'apertura: a tendina chiusa quell'id non
 *    corrisponde a niente, e manca `aria-expanded`, obbligatorio per il ruolo.
 * 2. NUMERI DI PAGINA — PrimeNG scrive la parola "Page" dentro la sostituzione
 *    (`pageLabel.replace(/{page}/g, `Page ${value}`)`), quindi con la
 *    traduzione italiana esce "Pagina Page 1".
 * 3. FINESTRE DI DIALOGO — `role="dialog"` senza nome accessibile, e non e'
 *    riparabile con aria-labelledby. Nel sorgente di PrimeNG la proprieta'
 *    `ariaLabelledBy` e' dichiarata ma non viene MAI assegnata, quindi
 *    `[attr.aria-labelledby]` non produce nulla; e l'id del titolo nasce da
 *    `[id]="getAriaLabelledBy()"`, cioe' `UniqueComponentId() + '_header'`
 *    rivalutato a ogni ciclo di rilevamento: **cambia di continuo**. Un
 *    riferimento per id sarebbe scaduto un istante dopo — provato, e infatti
 *    puntava a `pn_id_8_header` mentre il titolo era gia' un altro.
 *    Quindi si copia il TESTO in un aria-label, che PrimeNG non tocca.
 *    Dal template non si puo' fare: `p-dialog` espone solo `header` e
 *    `closeAriaLabel`.
 *
 * 4. PULSANTI DI CHIUSURA — la X di `p-dialog` e quella di `p-toast` escono da
 *    `[attr.aria-label]="closeAriaLabel"`, un @Input **senza valore predefinito**:
 *    se non lo si passa, il pulsante non ha nome. Nel progetto ci sono 43
 *    `p-dialog` e un `p-toast` in quasi ogni pagina, quindi passarlo a mano
 *    vorrebbe dire ripeterlo ovunque e dimenticarlo alla prossima finestra —
 *    oltre a non coprire i dialoghi che apre la libreria per conto suo.
 *
 * 6. VALUTAZIONE A STELLE (p-rating) — i cinque radio nascosti hanno tutti
 *    `[checked]="value === 0"`: nessuno risulta mai scelto dopo il voto, e prima
 *    del voto (valore 0) risultano scelti tutti, cioe' l'ultimo. Il lettore di
 *    schermo annunciava «5 stelle, selezionato» su un voto ancora da dare. Lo
 *    stato vero si ricava dalle stelle accese (`.p-rating-item-active`), che
 *    PrimeNG disegna bene. Scriverlo non richiama Angular: il legame su
 *    `checked` si riscrive solo se `value === 0` cambia, cioe' quasi mai.
 *
 * 7. VALUTAZIONE A STELLE, FUOCO — il modello di p-rating accende la classe
 *    p-focus con `isFocusVisible`, ma nella classe la proprieta' si chiama
 *    `isFocusVisibleItem` (falsa dopo un clic, vera quando si cambia voto da
 *    tastiera). `isFocusVisible` non esiste, quindi p-focus non si accendeva
 *    mai. Si aggiunge sul prototipo un alias in sola lettura, solo se la
 *    libreria non ce l'ha: il giorno che PrimeNG si corregge, questo tace.
 *    Secondo difetto: dopo un clic `isFocusVisibleItem` resta falsa finche'
 *    non si cambia voto con le frecce, quindi chi esce con Tab e rientra non
 *    vede dove si trova. Il fuoco che arriva da tastiera la rimette vera; il
 *    fuoco che il clic stesso sposta sul radio no. (`:focus-visible` qui non
 *    serve: Chrome lo accende anche su quel fuoco spostato dal clic.)
 *
 * 8. CELLE VUOTE DEGLI ELENCHI — sotto i 576px la tabella di PrimeNG diventa
 *    una pila di righe «titolo: valore», e una cella senza valore restava come
 *    riga con il solo titolo («Area» e niente accanto). Il CSS non puo'
 *    accorgersene: il valore e' spesso testo semplice, e i selettori non vedono
 *    i nodi di testo. Qui si segna la cella con `dt-cella-vuota` quando, a parte
 *    il titolo, non contiene ne' testo ne' un elemento che si vede da solo
 *    (icona, immagine, comando, campo); il foglio globale la nasconde solo
 *    sotto la soglia. Sopra resta al suo posto, o le colonne si sfalserebbero.
 *    Limite noto: con il solo `childList` una cella che si riempie cambiando il
 *    testo di un nodo gia' esistente non viene rivalutata. Negli elenchi non
 *    capita, perche' i dati nuovi arrivano con righe nuove.
 *
 * 9. STATO DI RADIO E CASELLE — p-radioButton e p-checkbox mettono sull'<input>
 *    nativo nascosto `[attr.aria-checked]`. Su un input radio o checkbox nativo
 *    aria-checked non e' ammesso: lo stato lo dice `checked`, che PrimeNG scrive
 *    gia' bene, e i lettori di schermo leggono quello. Si toglie l'attributo.
 *    Angular pero' lo riscrive ogni volta che la scelta cambia, e un cambio di
 *    attributo sfugge all'osservatore principale (solo `childList`, vedi sotto).
 *    Serve un secondo osservatore, ristretto al solo `aria-checked`: non fa
 *    scansioni del documento, guarda soltanto l'elemento cambiato. La nostra
 *    rimozione genera un'altra notifica, che trova l'attributo gia' tolto e si
 *    ferma li': niente ciclo. PrimeNG non usa aria-checked per disegnare lo stato.
 *
 * 10. BARRE DI AVANZAMENTO — p-progressBar disegna `role="progressbar"` con i
 *    valori, ma non ha un @Input per il nome: la barra resta senza nome
 *    accessibile. Il nome si scrive nel template sul tag, come
 *    `[attr.data-dt-nome]`, e qui si copia in aria-label sull'elemento col ruolo.
 *    La barra compare con *ngIf, quindi l'osservatore principale la vede.
 *
 * 5. FRECCIA DELLA TENDINA — esce con `role="button"` e
 *    `aria-label="dropdown trigger"`, ma senza tabindex: un pulsante che non si
 *    puo' premere. E' decorativa e va nascosta ai lettori di schermo.
 *
 * ATTENZIONE, LEZIONE IMPARATA IL 10/09/2026. La prima versione osservava anche
 * gli ATTRIBUTI di tutto il body e rifaceva la scansione a ogni mutazione. Su
 * pagine che rigenerano molto DOM — la cartella anamnestica, l'agenda del
 * medico — il browser si bloccava: ogni scrittura di attributo genera altre
 * mutazioni, e ogni mutazione faceva ripartire tre querySelectorAll sull'intero
 * documento. Da qui due vincoli da non togliere:
 *
 *   - si osserva SOLO `childList`. Le nostre scritture toccano attributi, che
 *     con questo filtro non richiamano l'osservatore: il ciclo e' impossibile
 *     per costruzione. Basta comunque, perche' PrimeNG aggiunge e toglie il
 *     pannello della lista dal DOM aprendo e chiudendo.
 *   - le mutazioni si accorpano in un solo passaggio per fotogramma, e il
 *     lavoro gira fuori dalla zona di Angular per non far ripartire il ciclo
 *     di rilevamento delle modifiche.
 */
@Injectable({ providedIn: 'root' })
export class AriaPrimengService {

    private osservatore: MutationObserver | null = null;
    private osservatoreStati: MutationObserver | null = null;
    private inCoda = false;

    constructor(private translate: TranslateService, private zone: NgZone) {}

    avvia(): void {
        if (this.osservatore) {
            return;
        }
        this.correggiFuocoValutazioni();
        this.zone.runOutsideAngular(() => {
            this.programma();
            this.osservatore = new MutationObserver(() => this.programma());
            this.osservatore.observe(document.body, { childList: true, subtree: true });
            // vedi la nota 9: solo aria-checked, e solo sull'elemento cambiato
            this.osservatoreStati = new MutationObserver((cambi) => {
                cambi.forEach((cambio) => this.togliStatoAria(cambio.target as Element));
            });
            this.osservatoreStati.observe(document.body, {
                attributes: true,
                attributeFilter: ['aria-checked'],
                subtree: true,
            });
        });
    }

    ferma(): void {
        this.osservatore?.disconnect();
        this.osservatore = null;
        this.osservatoreStati?.disconnect();
        this.osservatoreStati = null;
    }

    /** un solo passaggio per fotogramma, comunque tante siano le mutazioni */
    private programma(): void {
        if (this.inCoda) {
            return;
        }
        this.inCoda = true;
        requestAnimationFrame(() => {
            this.inCoda = false;
            this.correggi();
        });
    }

    private correggi(): void {
        this.correggiTendine();
        this.correggiNumeriDiPagina();
        this.correggiDialoghi();
        this.correggiChiusure();
        this.correggiFrecce();
        this.correggiValutazioni();
        this.segnaCelleVuote();
        this.nominaBarre();
        document.querySelectorAll('input[type="radio"][aria-checked], input[type="checkbox"][aria-checked]')
            .forEach((el) => this.togliStatoAria(el));
    }

    /** vedi la nota 10: il nome scritto sul tag passa all'elemento col ruolo */
    private nominaBarre(): void {
        document.querySelectorAll('p-progressbar[data-dt-nome]').forEach((tag) => {
            const nome = tag.getAttribute('data-dt-nome') || '';
            const barra = tag.querySelector('[role="progressbar"]');
            if (barra && nome && barra.getAttribute('aria-label') !== nome) {
                barra.setAttribute('aria-label', nome);
            }
        });
    }

    /** vedi la nota 9: sui radio e sulle caselle native lo stato lo dice `checked` */
    private togliStatoAria(el: Element): void {
        if (el instanceof HTMLInputElement
            && (el.type === 'radio' || el.type === 'checkbox')
            && el.hasAttribute('aria-checked')) {
            el.removeAttribute('aria-checked');
        }
    }

    /** vedi la nota 8: la cella «titolo e niente» si segna, il CSS la nasconde su telefono */
    private segnaCelleVuote(): void {
        const contenuto = 'img, svg, i, button, a, input, select, textarea, p-checkbox, p-inputswitch, p-rating';
        document.querySelectorAll<HTMLElement>('.p-datatable-tbody > tr > td').forEach((td) => {
            const titolo = td.querySelector(':scope > .p-column-title');
            if (!titolo) {
                return;
            }
            let vuota = true;
            for (const nodo of Array.from(td.childNodes)) {
                if (nodo === titolo || nodo.nodeType === Node.COMMENT_NODE) {
                    continue;
                }
                if (nodo.nodeType === Node.TEXT_NODE) {
                    if (nodo.textContent?.trim()) { vuota = false; break; }
                    continue;
                }
                const el = nodo as HTMLElement;
                if (el.textContent?.trim() || el.matches?.(contenuto) || el.querySelector?.(contenuto)) {
                    vuota = false;
                    break;
                }
            }
            if (td.classList.contains('dt-cella-vuota') !== vuota) {
                td.classList.toggle('dt-cella-vuota', vuota);
            }
        });
    }

    /** lo stato aperto lo scrive bene PrimeNG: qui si sistema solo il chiuso */
    private correggiTendine(): void {
        document.querySelectorAll('[role="combobox"]').forEach((el) => {
            const messoDaParte = el.getAttribute('data-aria-controls');
            const legame = el.getAttribute('aria-controls') || messoDaParte;
            const listaEsiste = !!(legame && document.getElementById(legame));

            if (listaEsiste) {
                if (!el.getAttribute('aria-controls') && messoDaParte) {
                    el.setAttribute('aria-controls', messoDaParte);
                }
                return;
            }
            if (el.getAttribute('aria-expanded') !== 'false') {
                el.setAttribute('aria-expanded', 'false');
            }
            // il valore va messo da parte: Angular non riscrive l'attributo
            // all'apertura, perche' l'espressione a cui e' legato non cambia
            if (legame && el.hasAttribute('aria-controls')) {
                el.setAttribute('data-aria-controls', legame);
                el.removeAttribute('aria-controls');
            }
        });
    }

    /** l'etichetta si ricostruisce dal numero scritto sul pulsante */
    private correggiNumeriDiPagina(): void {
        const pulsanti = document.querySelectorAll('.p-paginator-page');
        if (!pulsanti.length) {
            return;
        }
        const parola = this.translate.instant('Pagina') || 'Pagina';
        pulsanti.forEach((el) => {
            const numero = (el.textContent || '').trim();
            if (!numero) {
                return;
            }
            const atteso = `${parola} ${numero}`;
            if (el.getAttribute('aria-label') !== atteso) {
                el.setAttribute('aria-label', atteso);
            }
        });
    }

    /**
     * Il nome del dialogo e' il testo del suo titolo, copiato in un aria-label.
     * Se il titolo arriva da un pTemplate="header" la classe .p-dialog-title non
     * c'e', e allora si ripiega sulla prima intestazione dentro la testata — non
     * sull'intera testata, che conterrebbe anche il pulsante di chiusura e se lo
     * porterebbe dentro al nome.
     */
    private correggiDialoghi(): void {
        document.querySelectorAll('[role="dialog"], [role="alertdialog"]').forEach((d) => {
            const titolo = d.querySelector('.p-dialog-title')
                || d.querySelector('.p-dialog-header h1, .p-dialog-header h2, .p-dialog-header h3,'
                                 + '.p-dialog-header h4, .p-dialog-header h5, .p-dialog-header h6');
            // Terzo ripiego, per le testate scritte con pTemplate="header" che non
            // hanno ne' .p-dialog-title ne' un'intestazione — in questo progetto
            // sono quelle di agenda, preconsulto e privacy del consulto, fatte con
            // uno <span>. Si prende il testo della testata escludendo il gruppo dei
            // comandi, altrimenti nel nome finirebbe anche il pulsante di chiusura.
            let testo = (titolo?.textContent || '').trim();
            if (!testo) {
                const testata = d.querySelector('.p-dialog-header');
                if (testata) {
                    testo = Array.from(testata.children)
                        .filter((c) => !c.classList.contains('p-dialog-header-icons'))
                        .map((c) => (c.textContent || '').trim())
                        .join(' ')
                        .trim();
                }
            }
            if (!testo) {
                return;
            }
            if (d.getAttribute('aria-label') !== testo) {
                d.setAttribute('aria-label', testo);
            }
            // un riferimento per id qui non puo' funzionare (vedi la nota in testa):
            // se ne e' rimasto uno da una versione precedente, va tolto perche'
            // punta nel vuoto
            if (d.hasAttribute('aria-labelledby')) {
                d.removeAttribute('aria-labelledby');
            }
        });
    }

    /**
     * La X che chiude finestre, avvisi e pannelli. PrimeNG la lascia senza nome
     * quando non le si passa `closeAriaLabel` (vedi la nota 4 in testa): qui si
     * mette una volta sola, per tutte, anche per i dialoghi che apre la libreria.
     * Non si sovrascrive un nome gia' presente, cosi' chi ne vuole uno piu'
     * preciso puo' passarlo dal template.
     */
    private correggiChiusure(): void {
        const parola = this.translate.instant('Chiudi') || 'Chiudi';
        document.querySelectorAll(
            '.p-dialog-header-close, .p-toast-icon-close, .p-overlaypanel-close, .p-sidebar-close'
        ).forEach((el) => {
            if (!el.getAttribute('aria-label')) {
                el.setAttribute('aria-label', parola);
            }
        });
    }

    /** vedi la nota 7: il modello legge una proprieta' che la classe non ha */
    private correggiFuocoValutazioni(): void {
        const proto = Rating.prototype as any;
        if (!('isFocusVisible' in proto)) {
            Object.defineProperty(proto, 'isFocusVisible', {
                configurable: true,
                get(this: { isFocusVisibleItem: boolean }) {
                    return this.isFocusVisibleItem;
                },
            });
        }
        if (!proto.dtFuocoCorretto) {
            proto.dtFuocoCorretto = true;
            const clic = proto.onOptionClick;
            const fuoco = proto.onInputFocus;
            proto.onOptionClick = function (this: any, ...argomenti: unknown[]) {
                this.dtDaClic = true;
                try {
                    return clic.apply(this, argomenti);
                } finally {
                    this.dtDaClic = false;
                }
            };
            proto.onInputFocus = function (this: any, ...argomenti: unknown[]) {
                fuoco.apply(this, argomenti);
                if (!this.dtDaClic) {
                    this.isFocusVisibleItem = true;
                }
            };
        }
    }

    /** il radio scelto e' quello dell'ultima stella accesa (vedi la nota 6) */
    private correggiValutazioni(): void {
        document.querySelectorAll('.p-rating').forEach((r) => {
            const accese = r.querySelectorAll('.p-rating-item-active').length;
            const radio = r.querySelectorAll<HTMLInputElement>(
                '.p-rating-item:not(.p-rating-cancel-item) input[type="radio"]');
            radio.forEach((input, i) => {
                const scelto = i + 1 === accese;
                if (input.checked !== scelto) {
                    input.checked = scelto;
                }
            });
        });
    }

    /** la freccia e' decorativa: via il ruolo finto e l'etichetta inglese */
    private correggiFrecce(): void {
        document.querySelectorAll('.p-dropdown-trigger:not([aria-hidden="true"])').forEach((el) => {
            el.setAttribute('aria-hidden', 'true');
            el.removeAttribute('role');
            el.removeAttribute('aria-label');
            el.removeAttribute('aria-haspopup');
        });
    }
}
